import { useCallback, useState, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { mdiCheckCircleOutline, mdiAlertCircleOutline } from '@mdi/js'

import { useAppDispatch, useAppSelector } from 'app/hooks'

import theme from 'theme_'
import { commonMessages } from 'app/i18n/messages'
import { MdiIcon } from 'components/Icons'
import { selectedHomeSelector } from 'features/home/selectors'
import { fetchHomeThunk } from 'features/home/slice'
import type { LicenseType } from 'features/licenses/types'
import SuccessModal from 'components/Modal/SuccessModal'
import ConfirmModal from 'components/Modal/ConfirmModal'
import { LoaderOverlay } from 'components/Loader'
import { prettyLicenseDescription } from 'features/licenses/utils'
import { DATETIME_SHORT, formatTime } from 'app/utils/formatDate'
import { RegularModerate } from 'components/Typography/Labels'
import { HomeStateContext } from 'features/home/homeStateContext'
import {
  ApplyLicenseWrapper,
  ApplyLicenseButton,
  ApplyLicenseModalContent,
  ApplyLicenseDatesWrap,
  LicenseMedallionImg,
} from './styles'
import {
  fetchAvailableLicensesThunk,
  homeApplyLicenseThunk,
} from '../../../slice'
import { availableLicensesMessages } from '../../messages'
import { makePublicUrl } from 'app/utils/publicUrl'

const STACKED_LICENSES_OVERFLOW_CODE = 834
const tooMuchSubscriptionLicensesError = `Only a maximum duration of 5 years of Savant Essentials can be activated on a host.`

const licenseMedallionSrc = makePublicUrl('images/license-medallion.svg')

// TODO localise it
interface ApplyLicenseProps {
  original: LicenseType
}

function ApplyLicense({ original }: ApplyLicenseProps) {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()

  const [isConfirmApplyModalOpen, setIsConfirmApplyModalOpen] = useState(false)
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
  const [isHomeOfflineModalOpen, setIsHomeOfflineModalOpen] = useState(false)
  const [isApplying, setIsApplying] = useState(false)
  const [showError, setShowError] = useState<boolean | number>(false)
  const [activatedDate, setActivatedDate] = useState<number | null>()
  const [expiresDate, setExpiresDate] = useState<number | null>()

  const currentHome = useAppSelector(selectedHomeSelector.data)
  const homeName = currentHome?.name || 'no name'
  const { isOnline } = useContext(HomeStateContext)

  const licenseId = original.id
  const licenseCode = original.code
  const licenseName = `${prettyLicenseDescription(original.description)}, ${
    original.name
  }`

  const defaultErrorMessage = `Error applying License ${licenseCode} to ${homeName}`

  const handleClickApplyButton = () => {
    if (isOnline) {
      setIsConfirmApplyModalOpen(true)
    } else {
      setIsHomeOfflineModalOpen(true)
    }
  }

  const handleApplyLicense = useCallback(async () => {
    if (currentHome?.id && currentHome?.domainId && licenseId) {
      setIsApplying(true)
      setIsConfirmApplyModalOpen(false)
      try {
        const license: LicenseType = await dispatch(
          homeApplyLicenseThunk(currentHome.domainId, currentHome.id, licenseId)
        )
        setActivatedDate(license?.activated)
        setExpiresDate(license?.expiresAt)

        await dispatch(fetchHomeThunk(currentHome.id))
        await dispatch(
          fetchAvailableLicensesThunk(currentHome.id, currentHome.domainId)
        )
        if (license?.expiresAt) {
          setIsInfoModalOpen(true)
        }
      } catch (error: any) {
        setIsApplying(false)
        if (error?.code === STACKED_LICENSES_OVERFLOW_CODE) {
          console.log('Too much subscription licenses for a home')
          return setShowError(STACKED_LICENSES_OVERFLOW_CODE)
        }
        setShowError(true)
        console.log(defaultErrorMessage, error)
      } finally {
        setIsApplying(false)
      }
    }
  }, [
    dispatch,
    currentHome?.domainId,
    currentHome?.id,
    licenseId,
    defaultErrorMessage,
  ])

  return (
    <ApplyLicenseWrapper>
      <ApplyLicenseButton onClick={handleClickApplyButton}>
        <FormattedMessage {...commonMessages.apply} />
      </ApplyLicenseButton>

      {isApplying && <LoaderOverlay isFullScreen />}

      {isConfirmApplyModalOpen && !isApplying && (
        <ConfirmModal
          isOpen
          onDiscard={() => setIsConfirmApplyModalOpen(false)}
          onConfirm={handleApplyLicense}
          title={formatMessage(availableLicensesMessages.modalApplyTitle)}
          discardBtnTitle={formatMessage(commonMessages.cancel)}
          confirmBtnTitle={formatMessage(commonMessages.apply)}
          variant="errorFilled"
        >
          <LicenseMedallionImg src={licenseMedallionSrc} alt="" />
          <RegularModerate>
            <FormattedMessage
              {...availableLicensesMessages.modalApplyText}
              values={{ licenseName, homeName, br: <br /> }}
            />
          </RegularModerate>
        </ConfirmModal>
      )}

      {(isInfoModalOpen || showError) && !isApplying && (
        <SuccessModal
          isOpen
          confirmBtnTitle={formatMessage(commonMessages.close)}
          title={
            showError
              ? formatMessage(availableLicensesMessages.modalErrorTitle)
              : formatMessage(availableLicensesMessages.modalInfoTitle)
          }
          onConfirm={() => {
            setIsInfoModalOpen(false)
            setShowError(false)
            setActivatedDate(null)
            setExpiresDate(null)
          }}
        >
          <ApplyLicenseModalContent>
            {showError ? (
              <>
                <MdiIcon
                  path={mdiAlertCircleOutline}
                  color={theme.colors.error}
                  size={3}
                />
                <RegularModerate>
                  {showError === STACKED_LICENSES_OVERFLOW_CODE
                    ? tooMuchSubscriptionLicensesError
                    : defaultErrorMessage}
                </RegularModerate>
              </>
            ) : (
              <>
                <MdiIcon
                  path={mdiCheckCircleOutline}
                  color={theme.colors.green}
                  size={3}
                />
                <ApplyLicenseDatesWrap>
                  {activatedDate && (
                    <FormattedMessage
                      {...availableLicensesMessages.modalActivatedDate}
                      values={{ br: <br /> }}
                    />
                  )}
                  {expiresDate && (
                    <>
                      <br />
                      <FormattedMessage
                        {...availableLicensesMessages.modalExpiresDate}
                        values={{
                          br: <br />,
                          date: formatTime(expiresDate, DATETIME_SHORT),
                        }}
                      />
                    </>
                  )}
                </ApplyLicenseDatesWrap>
              </>
            )}
          </ApplyLicenseModalContent>
        </SuccessModal>
      )}
      {isHomeOfflineModalOpen && (
        <SuccessModal
          isOpen
          confirmBtnTitle={formatMessage(commonMessages.close)}
          title={formatMessage(availableLicensesMessages.modalErrorTitle)}
          onConfirm={() => setIsHomeOfflineModalOpen(false)}
        >
          <RegularModerate>
            <FormattedMessage
              {...availableLicensesMessages.modalHomeOfflineText}
              values={{ homeName }}
            />
          </RegularModerate>
        </SuccessModal>
      )}
    </ApplyLicenseWrapper>
  )
}

export default ApplyLicense
